import React from 'react';
import { revenueFeaturesData } from '../../data/distributorsData';
import { DollarSign, Package, FileCheck, TrendingUp } from 'lucide-react';

const iconMap = {
  DollarSign,
  Package,
  FileCheck
};

const RevenueSection = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Visual */}
          <div className="relative order-2 lg:order-1">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl">
              {/* Revenue Dashboard */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-white font-semibold">Ingresos por Reseller</span>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm">+35% este mes</span>
                </div>
              </div>

              {/* Top Resellers */}
              <div className="space-y-4 mb-6">
                {[
                  { name: 'TechCloud Solutions', revenue: '$45,200', growth: '+28%', tier: 'Gold' },
                  { name: 'DataServ Partners', revenue: '$38,500', growth: '+22%', tier: 'Silver' },
                  { name: 'CloudMaster Pro', revenue: '$32,100', growth: '+18%', tier: 'Gold' },
                  { name: 'InnovateTech', revenue: '$28,400', growth: '+15%', tier: 'Bronze' }
                ].map((reseller, idx) => (
                  <div key={idx} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#1297fd]/20 flex items-center justify-center">
                          <span className="text-[#40aaf8] font-bold">{reseller.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{reseller.name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            reseller.tier === 'Gold' ? 'bg-yellow-500/20 text-yellow-400' :
                            reseller.tier === 'Silver' ? 'bg-gray-400/20 text-gray-300' :
                            'bg-orange-500/20 text-orange-400'
                          }`}>
                            {reseller.tier}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{reseller.revenue}</p>
                        <p className="text-green-400 text-xs">{reseller.growth}</p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full" 
                        style={{ 
                          width: `${100 - idx * 15}%`,
                          background: 'linear-gradient(to right, #1297fd, #40aaf8)'
                        }} 
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <p className="text-white font-bold">$2.4M</p>
                  <p className="text-[#8bcaf9] text-xs">Total Mensual</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <p className="text-white font-bold">847</p>
                  <p className="text-[#8bcaf9] text-xs">Resellers</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center">
                  <p className="text-white font-bold">$2,834</p>
                  <p className="text-[#8bcaf9] text-xs">Promedio</p>
                </div>
              </div>
            </div>

            {/* Decorative */}
            <div className="absolute -top-4 -left-4 w-20 h-20 bg-[#8bcaf9]/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#1297fd]/20 rounded-full blur-2xl" />
          </div>

          {/* Right Content */}
          <div className="order-1 lg:order-2">
            <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
              Maximiza Ingresos
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Aumenta los ingresos{' '}
              <span className="text-[#1297fd]">por reseller</span>
            </h2>
            <p className="text-lg text-[#c3c3c5] mb-8 leading-relaxed">
              Da a los resellers la flexibilidad para vender más y maximizar sus ganancias. Nuestra plataforma automatiza modelos de precios, aplica descuentos por volumen y habilita bundling inteligente.
            </p>

            {/* Features List */}
            <div className="space-y-6">
              {revenueFeaturesData.map((feature) => {
                const IconComponent = iconMap[feature.icon];
                return (
                  <div key={feature.id} className="flex gap-4 group">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                      {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#1297fd] transition-colors">
                        {feature.title}
                      </h4>
                      <p className="text-[#c3c3c5] text-sm leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RevenueSection;
